/**
 * AIPreferences - AI and Literature Settings Page
 * ============================================================================
 * DC-957: User preferences for AI-generated recommendations, including
 * literature citation settings.
 *
 * Features:
 * - Toggle to enable/disable literature citations in recommendations
 * - Max citations per recommendation setting
 * - Preferences persisted to localStorage
 *
 * @version 1.0.0
 * @created 2026-01-24
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { BRANDING } from '../../config/branding';
import './ai-preferences.css';

// ============================================================================
// Types
// ============================================================================

export interface AIPreferencesState {
  /** Enable literature citations in recommendations */
  literatureEnabled: boolean;
  /** Maximum number of citations per recommendation (1-10) */
  maxCitationsPerRecommendation: number;
  /** Show literature confidence badges */
  showConfidenceBadges: boolean;
  /** Default collapsed state for citation lists */
  defaultCollapsedCitations: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'faircompass_ai_preferences';

const DEFAULT_PREFERENCES: AIPreferencesState = {
  literatureEnabled: true,
  maxCitationsPerRecommendation: 3,
  showConfidenceBadges: true,
  defaultCollapsedCitations: true,
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Load preferences from localStorage
 */
export function loadAIPreferences(): AIPreferencesState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('[AIPreferences] Failed to load preferences:', e);
  }
  return DEFAULT_PREFERENCES;
}

/**
 * Save preferences to localStorage
 */
function saveAIPreferences(preferences: AIPreferencesState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch (e) {
    console.warn('[AIPreferences] Failed to save preferences:', e);
  }
}

// ============================================================================
// Main Component
// ============================================================================

export const AIPreferences: React.FC = () => {
  const [preferences, setPreferences] = useState<AIPreferencesState>(DEFAULT_PREFERENCES);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Load preferences on mount
  useEffect(() => {
    setPreferences(loadAIPreferences());
  }, []);

  // Handle preference changes
  const handleChange = (key: keyof AIPreferencesState, value: boolean | number) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);
    setSaveStatus('saving');

    try {
      saveAIPreferences(updated);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  return (
    <div className="ai-preferences">
      <div className="ai-preferences__header">
        <h2>AI Preferences</h2>
        <p className="ai-preferences__subtitle">
          {`Configure how ${BRANDING.productName} AI generates recommendations and integrates scientific literature.`}
        </p>
        {saveStatus !== 'idle' && (
          <span className={`ai-preferences__save-status ai-preferences__save-status--${saveStatus}`}>
            {saveStatus === 'saving' && 'Saving...'}
            {saveStatus === 'saved' && 'Saved'}
            {saveStatus === 'error' && 'Failed to save'}
          </span>
        )}
      </div>

      <div className="ai-preferences__section">
        <h3>Literature Citations</h3>
        <p className="ai-preferences__section-desc">
          Control how scientific literature is integrated into AI recommendations.
          Citations are sourced from PubMed and curated research papers.
        </p>

        {/* Literature Enabled Toggle */}
        <div className="ai-preferences__item">
          <div className="ai-preferences__item-header">
            <label htmlFor="literatureEnabled" className="ai-preferences__label">
              Include Literature Citations
            </label>
            <span className="ai-preferences__badge">Recommended</span>
          </div>
          <p className="ai-preferences__item-desc">
            When enabled, recommendations will include relevant scientific papers
            from PubMed to support the guidance provided.
          </p>
          <label className="ai-preferences__toggle">
            <input
              type="checkbox"
              id="literatureEnabled"
              checked={preferences.literatureEnabled}
              onChange={(e) => handleChange('literatureEnabled', e.target.checked)}
            />
            <span className="ai-preferences__toggle-slider"></span>
            <span className="ai-preferences__toggle-label">
              {preferences.literatureEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>

        {/* Max Citations Setting */}
        <div className={`ai-preferences__item ${!preferences.literatureEnabled ? 'ai-preferences__item--disabled' : ''}`}>
          <label htmlFor="maxCitations" className="ai-preferences__label">
            Maximum Citations per Recommendation
          </label>
          <p className="ai-preferences__item-desc">
            Limit the number of literature citations shown for each recommendation.
            More citations provide better evidence but may increase visual complexity.
          </p>
          <div className="ai-preferences__number-input">
            <input
              type="number"
              id="maxCitations"
              min={1}
              max={10}
              value={preferences.maxCitationsPerRecommendation}
              onChange={(e) => handleChange('maxCitationsPerRecommendation', parseInt(e.target.value, 10) || 3)}
              disabled={!preferences.literatureEnabled}
            />
            <span className="ai-preferences__number-hint">1-10 citations</span>
          </div>
        </div>

        {/* Show Confidence Badges */}
        <div className={`ai-preferences__item ${!preferences.literatureEnabled ? 'ai-preferences__item--disabled' : ''}`}>
          <label htmlFor="showConfidenceBadges" className="ai-preferences__label">
            Show Confidence Badges
          </label>
          <p className="ai-preferences__item-desc">
            Display confidence level badges (High, Moderate, Expert Guidance) on
            literature citation sections.
          </p>
          <label className="ai-preferences__toggle">
            <input
              type="checkbox"
              id="showConfidenceBadges"
              checked={preferences.showConfidenceBadges}
              onChange={(e) => handleChange('showConfidenceBadges', e.target.checked)}
              disabled={!preferences.literatureEnabled}
            />
            <span className="ai-preferences__toggle-slider"></span>
            <span className="ai-preferences__toggle-label">
              {preferences.showConfidenceBadges ? 'Show' : 'Hide'}
            </span>
          </label>
        </div>

        {/* Default Collapsed State */}
        <div className={`ai-preferences__item ${!preferences.literatureEnabled ? 'ai-preferences__item--disabled' : ''}`}>
          <label htmlFor="defaultCollapsed" className="ai-preferences__label">
            Start Citations Collapsed
          </label>
          <p className="ai-preferences__item-desc">
            When enabled, literature citation sections start collapsed to reduce
            visual noise. Users can expand to see details.
          </p>
          <label className="ai-preferences__toggle">
            <input
              type="checkbox"
              id="defaultCollapsed"
              checked={preferences.defaultCollapsedCitations}
              onChange={(e) => handleChange('defaultCollapsedCitations', e.target.checked)}
              disabled={!preferences.literatureEnabled}
            />
            <span className="ai-preferences__toggle-slider"></span>
            <span className="ai-preferences__toggle-label">
              {preferences.defaultCollapsedCitations ? 'Collapsed' : 'Expanded'}
            </span>
          </label>
        </div>
      </div>

      <div className="ai-preferences__footer">
        <p className="ai-preferences__footer-note">
          Changes are automatically saved and will apply to new recommendations.
          Existing recommendations will update on page refresh.
        </p>
      </div>
    </div>
  );
};

export default AIPreferences;
